import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { templatesApi } from "../../api/endpoints";
import type { Stage } from "../../types/domain";
import { IcoCheck, IcoClose, IcoTemplate } from "../../components/icons";
import { ModalBackdrop } from "../../components/ModalBackdrop";

const STAGE_META: Record<Stage, { label: string; color: string }> = {
  warmup: { label: "Прогрев", color: "var(--stage-warmup)" },
  sales: { label: "Продажи", color: "var(--stage-sales)" },
  close: { label: "Дожим", color: "var(--stage-close)" },
};

interface Seed {
  typeId: string;
  title: string;
  preview: string;
  time: string;
}

interface Props {
  seed: Seed;
  defaultStage: Stage;
  onClose: () => void;
  onSaved?: () => void;
}

export function SaveAsTemplateDialog({ seed, defaultStage, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState(seed.title);
  const [stage, setStage] = useState<Stage>(defaultStage);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  const addTagFromInput = () => {
    const v = tagInput.trim();
    if (!v) return;
    if (!tags.includes(v)) setTags([...tags, v]);
    setTagInput("");
  };

  const removeTag = (t: string) => setTags(tags.filter((x) => x !== t));

  const createMutation = useMutation({
    mutationFn: () =>
      templatesApi.create({
        name: name.trim(),
        typeId: seed.typeId,
        stage,
        subject: seed.title,
        preview: seed.preview,
        time: seed.time || null,
        tags,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["templates"] });
      onSaved?.();
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const canSave = name.trim().length > 0 && seed.typeId;

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="modal modal-narrow" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="launch-dot" style={{ background: "var(--accent-soft)" }}>
            <IcoTemplate size={12} stroke="var(--accent-text)" />
          </div>
          <h2>Сохранить как шаблон</h2>
          <div className="spacer" />
          <button type="button" className="modal-close" onClick={onClose}>
            <IcoClose size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12.5, color: "var(--text-3)", margin: "0 0 14px" }}>
            Шаблон появится в разделе «Шаблоны писем» — сможете применять его в других запусках.
          </p>

          <div className="form-row">
            <label className="form-label">Название шаблона</label>
            <input
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="Например: Напоминание за 3 часа"
            />
          </div>

          <div className="form-row">
            <label className="form-label">Стадия запуска</label>
            <div className="seg seg-inline" style={{ width: "100%" }}>
              {(Object.entries(STAGE_META) as Array<[Stage, { label: string; color: string }]>).map(
                ([k, m]) => (
                  <button
                    type="button"
                    key={k}
                    className={stage === k ? "active" : ""}
                    onClick={() => setStage(k)}
                    style={{ flex: 1 }}
                  >
                    <span className="stage-dot" style={{ background: m.color }} />
                    {m.label}
                  </button>
                ),
              )}
            </div>
          </div>

          <div className="form-row">
            <label className="form-label">Теги (необязательно)</label>
            <div className="templates-tag-editor">
              {tags.map((t) => (
                <span key={t} className="templates-tag templates-tag-edit">
                  #{t}
                  <button type="button" onClick={() => removeTag(t)} aria-label="Удалить тег">
                    <IcoClose size={10} />
                  </button>
                </span>
              ))}
              <input
                className="templates-tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addTagFromInput();
                  }
                }}
                onBlur={addTagFromInput}
                placeholder={tags.length === 0 ? "напр.: напоминание" : "+"}
              />
            </div>
          </div>

          {error && (
            <div style={{ color: "var(--text-danger, #DC2626)", fontSize: 12.5 }}>{error}</div>
          )}
        </div>
        <div className="modal-foot">
          <div className="spacer" />
          <button type="button" className="btn-ghost" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              if (!canSave || createMutation.isPending) return;
              setError(null);
              createMutation.mutate();
            }}
            disabled={!canSave || createMutation.isPending}
          >
            <IcoCheck size={14} /> Сохранить
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
